import type { ComponentProps, PropsWithChildren } from "react";
import { Dialog } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import type { SvgSlot } from "@/shared/ui";
import { Heading } from "@/shared/ui/common/heading";
import { CircleIcon, CloseIcon } from "../icons";

const styles = tv({
  slots: {
    overlay:
      "fixed inset-0 z-50 bg-neutral-200/60 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
    content:
      "fixed top-[50%] left-[50%] z-50 w-full translate-x-[-50%] translate-y-[-50%] rounded-2xl bg-white shadow-lg duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:[&_[data-slot=dialog-icon]]:animate-icon-out data-[state=open]:[&_[data-slot=dialog-icon]]:animate-icon-in",
    scrollArea:
      "max-h-[calc(100dvh_-_3rem)] overflow-y-auto rounded-[inherit] [scrollbar-color:theme(colors.neutral.500)_transparent] [scrollbar-width:thin]",
    body: "flex flex-col gap-8 px-8 pt-10 pb-8 [&_[data-slot=dialog-title]]:text-neutral-950",
    close:
      "absolute top-3 right-3 size-5 rounded-xs text-neutral-950 opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none [&_svg]:pointer-events-none",
    header: "flex flex-col items-center gap-6 text-center",
    footer: "flex justify-center gap-3.5",
    description: "font-medium text-neutral-800",
  },
  variants: {
    size: {
      large: {
        content: "max-w-4xl",
      },
      small: {
        content: "max-w-xl",
      },
    },
  },
  defaultVariants: {
    size: "small",
  },
});

const { overlay, scrollArea, header, footer, description } = styles();

const Root = ({ ...props }: ComponentProps<typeof Dialog.Root>) => {
  return <Dialog.Root data-slot="dialog" {...props} />;
};

const Trigger = ({ ...props }: ComponentProps<typeof Dialog.Trigger>) => {
  return <Dialog.Trigger data-slot="dialog-trigger" {...props} />;
};

const Portal = ({ ...props }: ComponentProps<typeof Dialog.Portal>) => {
  return <Dialog.Portal data-slot="dialog-portal" {...props} />;
};

const Close = ({ ...props }: ComponentProps<typeof Dialog.Close>) => {
  return <Dialog.Close data-slot="dialog-close" {...props} />;
};

const Overlay = ({
  className,
  ...props
}: ComponentProps<typeof Dialog.Overlay>) => {
  return (
    <Dialog.Overlay
      data-slot="dialog-overlay"
      className={overlay({ className })}
      {...props}
    />
  );
};

type ContentProps = Omit<ComponentProps<typeof Dialog.Content>, "className"> &
  VariantProps<typeof styles> & {
    withCloseButton?: boolean;
  };

const Content = ({
  children,
  withCloseButton = true,
  size,
  ...props
}: ContentProps) => {
  const { content, body, close } = styles({ size });

  return (
    <Portal data-slot="dialog-portal">
      <Overlay />
      <Dialog.Content
        data-slot="dialog-content"
        className={content()}
        {...props}
      >
        <div className={scrollArea()}>
          <div className={body()}>{children}</div>
        </div>

        {withCloseButton && (
          <Dialog.Close data-slot="dialog-close" className={close()}>
            <CloseIcon />
            <span className="sr-only">Close</span>
          </Dialog.Close>
        )}
      </Dialog.Content>
    </Portal>
  );
};

const Header = ({
  className,
  children,
  icon,
  ...props
}: ComponentProps<"div"> & { icon?: typeof SvgSlot }) => {
  return (
    <div
      data-slot="dialog-header"
      className={header({ className: "gap-2" })}
      {...props}
    >
      {icon && <CircleIcon data-slot="dialog-icon" icon={icon} />}

      <div className={header({ className })}>{children}</div>
    </div>
  );
};

const Footer = ({ className, ...props }: ComponentProps<"div">) => {
  return (
    <div
      data-slot="dialog-footer"
      className={footer({ className })}
      {...props}
    />
  );
};

const Title = ({ ...props }: PropsWithChildren) => {
  return (
    <Dialog.DialogTitle asChild>
      <Heading level={2} size="large" data-slot="dialog-title" {...props} />
    </Dialog.DialogTitle>
  );
};

const Description = ({
  className,
  ...props
}: ComponentProps<typeof Dialog.Description>) => {
  return (
    <Dialog.Description
      data-slot="dialog-description"
      className={description({ className })}
      {...props}
    />
  );
};

export const DialogPrimitive = {
  Root,
  Trigger,
  Portal,
  Close,
  Overlay,
  Content,
  Header,
  Footer,
  Title,
  Description,
};
